import { Team } from '@tassomutuo-backoffice/types';
import { Box, Typography, Divider, Avatar } from '@mui/material';
import React from 'react';
import { CtaButton, ModalContainer } from '@tassomutuo-backoffice/ui';
import { Mail, Person } from '@mui/icons-material';
import GroupIcon from '@mui/icons-material/Group';
import PersonIcon from '@mui/icons-material/Person';
import { useMutation } from '@tanstack/react-query';
import { sendNotificationAlertTeamManager } from '../../../../services/api/team/team';
import { toast } from 'react-toastify';

interface IProps {
  open: boolean;
  handleClose: () => void;
  teamToNotify: Team;
}

const TeamConfirmNotifyModal = ({
  open,
  handleClose,
  teamToNotify,
}: IProps) => {
  const sendNotificationTeamManagerMutation = useMutation({
    mutationFn: ({
      teamId,
      includeChief,
    }: {
      teamId: string;
      includeChief: boolean;
    }) => sendNotificationAlertTeamManager(teamId, includeChief),
    onSuccess: (data, variables, context) => {
      toast.success('Notifica inviata');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const closeModalHandler = () => {
    handleClose();
  };

  const onConfirmHandler = async (includeChief: boolean = false) => {
    await sendNotificationTeamManagerMutation.mutateAsync({
      teamId: teamToNotify.id,
      includeChief,
    });
    closeModalHandler();
  };

  const isLoading = sendNotificationTeamManagerMutation.isPending;

  return (
    <ModalContainer
      open={open}
      handleClose={isLoading ? () => {} : closeModalHandler}
      containerSx={{ minWidth: '500px' }}
    >
      <Box sx={{ textAlign: 'center' }}>
        <Box sx={{ mb: 3 }}>
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: 'primary.main',
              mx: 'auto',
              mb: 2,
            }}
          >
            <Mail fontSize="large" />
          </Avatar>
          <Typography
            variant="h5"
            fontWeight="bold"
            color="text.primary"
            gutterBottom
          >
            Invio Notifica Email
          </Typography>
        </Box>

        <Divider sx={{ mb: 3 }} />

        <Box sx={{ mb: 4 }}>
          <Typography variant="body1" color="text.secondary" mb={2}>
            Stai per inviare una notifica al team manager:
          </Typography>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 1,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'grey.200',
            }}
          >
            <Person color="primary" />
            <Typography variant="h6" fontWeight="bold" color="primary.main">
              {teamToNotify.teamManager.name}
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2,
            justifyContent: 'center',
            alignItems: 'center',
          }}
        >
          <CtaButton
            type="button"
            variant="contained"
            startIcon={<PersonIcon />}
            loading={isLoading}
            onClick={() => onConfirmHandler(false)}
            sx={{
              minWidth: 180,
              py: 1.5,
              fontSize: '0.95rem',
              textTransform: 'none',
            }}
          >
            Solo Team Manager
          </CtaButton>

          <CtaButton
            type="button"
            variant="contained"
            startIcon={<GroupIcon />}
            loading={isLoading}
            onClick={() => onConfirmHandler(true)}
            color="error"
            sx={{
              minWidth: 180,
              py: 1.5,
              fontSize: '0.95rem',
              textTransform: 'none',
            }}
          >
            Includi i soci in cc
          </CtaButton>
        </Box>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 3, display: 'block' }}
        >
          La notifica verrà inviata immediatamente dopo la conferma
        </Typography>
      </Box>
    </ModalContainer>
  );
};

export default TeamConfirmNotifyModal;
